import { SnMapClusterPoint } from '@lib/api';

import { IMapMarkerOptions } from '../../core/marker/marker-options';
import { IMarkerEventHandlersConfig } from './marker.interface';

export interface IMarkerOptions extends IMapMarkerOptions {
  // Facade-specific options based on current usage patterns
  
  // Required title for accessibility (inherits from IMapMarkerOptions but making it explicit)
  title: string; // Required for automatic accessibility generation
  
  // Cluster management
  cluster?: SnMapClusterPoint;
  
  // Animation and visual effects
  animationClass?: string;
  animationOn?: boolean;
  
  // Performance and optimization
  priority?: 'low' | 'normal' | 'high';
  lazyLoad?: boolean;
  
  // Badge and notification options
  showBadge?: boolean;
  badgePosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  notificationCount?: number;
  
  // Popup and tooltip options
  showPopupOnHover?: boolean;
  showTooltipOnHover?: boolean;
  popupDelay?: number;
  
  // Event handling configuration
  eventHandlers?: IMarkerEventHandlersConfig;
  
  // Initial state (these will be moved to the marker instance after creation)
  initialSelected?: boolean;
  initialHighlighted?: boolean;
  initialFocused?: boolean;
  
  // Validation and constraints
  minZoom?: number;
  maxZoom?: number;
  visibleAtZoom?: number;
  
  // Performance tuning
  renderThreshold?: number;
  updateFrequency?: number;
  
  // Lifecycle hooks
  onBeforeCreate?: () => void;
  onAfterCreate?: () => void;
  onBeforeDestroy?: () => void;
  onAfterDestroy?: () => void;
}

// Default marker options for consistent behavior
export const DEFAULT_MARKER_OPTIONS: Partial<IMarkerOptions> = {
  // Performance defaults
  priority: 'normal',
  lazyLoad: false,
  
  // Visual defaults
  showBadge: false,
  showPopupOnHover: false,
  showTooltipOnHover: false,
  popupDelay: 300,
  
  // Performance tuning defaults
  renderThreshold: 0,
  updateFrequency: 16, // ~60fps
  
  // Validation defaults
  minZoom: 0,
  maxZoom: 24,
};

// Helper type for creating marker options with validation
export type IMarkerOptionsConfig = Partial<IMarkerOptions>;

// Validation helper for marker options
export interface IMarkerOptionsValidator {
  validate(options: IMarkerOptionsConfig): string[];
  sanitize(options: IMarkerOptionsConfig): IMarkerOptions;
}
